import { ARRIVAL_METHOD_VALUES, type ArrivalMethod } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, Field, Icon, Select, Switch } from "@sagot/ui";
import { useId, useState } from "react";
import { Gate, useCanManage } from "../panels/panel-kit";
import { ALL_ON, arrivalOf, changedAgents, draftIsSavable, type ArrivalDraft } from "./launch-settings-model";
import { useSaveLaunchSettings, type LaunchSettings } from "./use-launch-settings";

const K = "launch.arrival";

function SettingRow({ id, title, hint, checked, disabled, onChange, testId }: { id: string; title: string; hint: string; checked: boolean; disabled: boolean; onChange: (on: boolean) => void; testId: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <label htmlFor={id} className="min-w-0 text-sm">
        <span id={`${id}-name`} className="font-semibold">{title}</span>
        <span id={`${id}-desc`} className="block text-fg-muted">{hint}</span>
      </label>
      <Switch id={id} aria-labelledby={`${id}-name`} aria-describedby={`${id}-desc`} checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

/** What the operator's server changes when the link is bound to the tab that started it. */
function StateBoundNote({ mintUrl }: { mintUrl: string | null }) {
  const { t } = useTranslation();
  return (
    <div role="note" className="flex items-start gap-2.5 rounded-md bg-info-soft p-3 text-sm" data-testid="state-bound-note">
      <Icon name="info" className="mt-0.5 size-4 shrink-0 text-info" />
      <div className="min-w-0 space-y-1.5">
        <p className="font-semibold">{t(`${K}.stateChangeTitle`)}</p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>{t(`${K}.stateStep1`)}</li>
          <li>{t(`${K}.stateStep2`)}</li>
          <li>{t(`${K}.stateStep3`)}</li>
        </ol>
        {mintUrl ? null : (
          <p className="font-semibold text-warning-ink" data-testid="state-needs-mint">
            {t(`${K}.stateNeedsMint`)}
          </p>
        )}
      </div>
    </div>
  );
}

function MethodRows({ agentId, draft, setDraft, canEdit, current }: { agentId: string; draft: ArrivalDraft; setDraft: (next: ArrivalDraft) => void; canEdit: boolean; current: Record<ArrivalMethod, boolean> }) {
  const { t } = useTranslation();
  const base = useId();
  return (
    <div className="divide-y divide-line">
      {ARRIVAL_METHOD_VALUES.map((method) => {
        const lastOn = current[method] && Object.values(current).filter(Boolean).length === 1;
        return (
          <SettingRow
            key={method}
            id={`${base}-${method}`}
            testId={`arrival-${method}`}
            title={t(`${K}.${method}Title`)}
            hint={lastOn ? t(`${K}.lastWayIn`) : t(`${K}.${method}Hint`)}
            checked={current[method]}
            disabled={!canEdit || lastOn}
            onChange={(on) => { setDraft({ ...draft, [agentId]: { ...current, [method]: on } }); }}
          />
        );
      })}
    </div>
  );
}

/**
 * Secure launch "How customers arrive" card (mockup secure-launch pin 1, PID-001, PID-007): per agent, which ways in are open
 * (launch from your login, e-mail code, open link) and, for the whole workspace, whether a launch link is bound to the tab
 * that started it. Turning a way off is enforced by the hosted chat: with the open link off an anonymous visitor is refused.
 */
export function ArrivalMethodsCard({ settings }: { settings: LaunchSettings }) {
  const { t } = useTranslation();
  const manage = useCanManage("customers.manage_launch");
  const saver = useSaveLaunchSettings(t(`${K}.saved`));
  const agentSelect = useId();
  const [picked, setPicked] = useState<string | null>(null);
  const [draft, setDraft] = useState<ArrivalDraft>({});
  const [stateBound, setStateBound] = useState<boolean | null>(null);
  const agents = settings.agents;
  const agentId = picked && agents.some((agent) => agent.agent_id === picked) ? picked : (agents[0]?.agent_id ?? null);
  const agent = agents.find((candidate) => candidate.agent_id === agentId);
  const changes = changedAgents(agents, draft);
  const stateChanged = stateBound !== null && stateBound !== settings.state_bound;
  const dirty = changes.length > 0 || stateChanged;
  const savable = draftIsSavable(draft);
  const reset = () => {
    setDraft({});
    setStateBound(null);
    saver.clearFailure();
  };
  const save = async () => {
    const saved = await saver.save({ ...(changes.length > 0 ? { agents: changes } : {}), ...(stateChanged ? { state_bound: stateBound } : {}) });
    if (saved) reset();
  };
  const current = agent ? { ...ALL_ON, ...arrivalOf(agents, draft, agent.agent_id) } : ALL_ON;
  const boundNow = stateBound ?? settings.state_bound;
  const verifiedOnly = !current.open_link && !current.email_code;
  return (
    <section className="card" aria-labelledby="arrival-title" data-testid="arrival-card">
      <div className="card-header flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="arrival-title" className="card-title">
            {t(`${K}.title`)}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">{t(`${K}.subtitle`)}</p>
        </div>
        {agent && verifiedOnly ? (
          <Badge tone="success" dot data-testid="arrival-verified-only">
            {t(`${K}.verifiedOnly`)}
          </Badge>
        ) : null}
      </div>
      {agent === undefined ? (
        <EmptyState icon="bot" headingLevel={3} title={t(`${K}.noAgentsTitle`)} description={t(`${K}.noAgentsBody`)} />
      ) : (
        <div className="space-y-2 px-5 py-4">
          {agents.length > 1 ? (
            <Field label={t(`${K}.agent`)} id={agentSelect} hint={t(`${K}.agentHint`)}>
              <Select value={agent.agent_id} onChange={(event) => { setPicked(event.target.value); }} data-testid="arrival-agent">
                {agents.map((candidate) => (
                  <option key={candidate.agent_id} value={candidate.agent_id}>
                    {candidate.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <p className="text-sm" data-testid="arrival-agent-name">
              <span className="text-fg-muted">{t(`${K}.agent`)}: </span>
              <b>{agent.name}</b>
            </p>
          )}
          <MethodRows agentId={agent.agent_id} draft={draft} setDraft={setDraft} canEdit={manage.allowed} current={current} />
          <div className="divide-y divide-line border-t border-line">
            <SettingRow id="arrival-state-bound" testId="arrival-state-bound" title={t(`${K}.stateTitle`)} hint={t(`${K}.stateHint`)} checked={boundNow} disabled={!manage.allowed} onChange={setStateBound} />
          </div>
          {boundNow ? <StateBoundNote mintUrl={settings.mint_url} /> : null}
          {manage.allowed ? null : <p className="text-xs text-fg-muted" data-testid="arrival-read-only">{manage.sessionReadOnly ? manage.reason : t(`${K}.readOnly`)}</p>}
          {saver.failure ? (
            <p role="alert" className="text-xs font-semibold text-danger" data-testid="arrival-error">
              {saver.failure}
            </p>
          ) : null}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
        <Button variant="secondary" disabled={!dirty || saver.busy} onClick={reset}>
          {t(`${K}.discard`)}
        </Button>
        <Gate reason={manage.reason}>
          <Button loading={saver.busy} disabled={!manage.allowed || !dirty || !savable} onClick={() => void save()}>
            {saver.busy ? t(`${K}.saving`) : t(`${K}.save`)}
          </Button>
        </Gate>
      </div>
    </section>
  );
}
